import { useRef, useState } from 'preact/hooks'
import { Card, MediaModal } from '@nespresso-design-system/components'

import ModalContainer from '@commons/modal/modal-container/ModalContainer'
import { Modals } from '@commons/modal/modal-container/types'
import { CampaignProps, LayoutProps } from '@commons/types'
import { PbPropsProvider, usePbProps } from '@content/helpers/src/context'
import { componentInteraction } from '@content/helpers/src/gtmEvents'
import { getPBProps } from '@content/helpers/src/utils.ts'
import { ModalProvider, useModal } from './ModalContext'
import styles from './ProductPortfolio.module.scss'
import ctaAdditionalStyles from '@commons/cta/cta.additional.module.scss'
import {
    ActionsProps,
    Cards,
    CTAPropsWithModal,
    HeadingProps,
    PBProps
} from './types.ts'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker.ts'
import CTA from '@commons/cta/CTA.tsx'

type ProductPortfolioProps = {
    data: string
}

type ModalFooterLabel = {
    machineFooterLabel: boolean
    coffeeFooterLabel: boolean
}

const RenderModalFooterCTA = ({ ...props }: CTAPropsWithModal) => {
    const btnRef = useRef(null)

    const handleClick = () => {
        componentInteraction(
            'fs_product_portfolio',
            'modal link click',
            props.label
        )
    }

    return <CTA {...props} onClick={handleClick} ref={btnRef} />
}

const RenderCards = ({ cards, modals }: { cards: Cards[]; modals: Modals }) => {
    const { isOpen, closeModal } = useModal()
    const { layout }: { layout: LayoutProps } = usePbProps()

    const coffeeModal = modals?.find(item => item.id === 'coffees'),
        machineModal = modals?.find(item => item.id === 'machines')

    const modalFooterLabel: ModalFooterLabel = {
        machineFooterLabel: !!machineModal?.cta_footer?.label,
        coffeeFooterLabel: !!coffeeModal?.cta_footer?.label
    }

    return (
        <>
            <div
                className={`${styles.productPortfolio__cards} ${styles['productPortfolio__cards__contrast_' + layout.contrast]}`}>
                {cards.map((card, i) => (
                    <RenderCard {...card} key={i} />
                ))}
            </div>

            {coffeeModal && (
                <MediaModal
                    className={styles.modalContainer}
                    id="coffees"
                    heading={coffeeModal?.heading}
                    description={coffeeModal?.description}
                    closeBtnA11yLabel={
                        coffeeModal?.translations?.closeBtnA11yLabel
                    }
                    closeBtnA11yTitle={
                        coffeeModal?.translations?.closeBtnA11yTitle
                    }
                    position="side"
                    isOpen={isOpen('coffees')}
                    onClose={closeModal}
                    fixedFooter={true}
                    footerSlot={
                        modalFooterLabel.coffeeFooterLabel ? (
                            <RenderModalFooterCTA
                                {...coffeeModal?.cta_footer}
                            />
                        ) : (
                            ''
                        )
                    }
                    contentSlot={
                        <ModalContainer
                            tracking_slot={'fs_product_portfolio'}
                            content={coffeeModal}
                        />
                    }
                    heroAspectRatio="16/9"
                />
            )}

            {machineModal && (
                <MediaModal
                    id="machines"
                    heading={machineModal?.heading}
                    description={machineModal?.description}
                    className={`${styles.modalContainer} ${styles.modalContainerWithoutFooter}`}
                    closeBtnA11yLabel={
                        machineModal?.translations?.closeBtnA11yLabel
                    }
                    closeBtnA11yTitle={
                        machineModal?.translations?.closeBtnA11yTitle
                    }
                    position="side"
                    isOpen={isOpen('machines')}
                    onClose={closeModal}
                    footerSlot={
                        modalFooterLabel.machineFooterLabel ? (
                            <RenderModalFooterCTA
                                {...machineModal?.cta_footer}
                            />
                        ) : (
                            ''
                        )
                    }
                    fixedFooter={true}
                    contentSlot={
                        <ModalContainer
                            tracking_slot={'fs_product_portfolio'}
                            content={machineModal}
                        />
                    }
                    heroAspectRatio="16/9"
                />
            )}
        </>
    )
}

const RenderCTA = ({
    index,
    campaign,
    ...props
}: CTAPropsWithModal & { index: number; campaign: CampaignProps }) => {
    const { openModal } = useModal()

    const btnClass =
        props?.variant === 'secondary' ? ctaAdditionalStyles.cta__custom : ''
    if (!props.link || !props.label) {
        return null
    }

    const handleActions = () => {
        if (props?.config.popin_id) {
            openModal(props?.config.popin_id)
        } else if (props.link) {
            window.location.href = props.link
        }
    }
    const handleClick = e => {
        e.preventDefault()
        handleActions()
    }

    const handleEnterPress = event => {
        if (event.key === 'Enter') {
            handleActions()
        }
    }

    return (
        <CTA
            {...props}
            isDecorative={true}
            className={`${btnClass} ${styles.btnWidth}`}
            tracking_select_promotion={`LINK - ${index} - ${props.label}`}
            campaign={campaign}
            onClick={handleClick}
            tabIndex={0}
            role="button"
            onKeyDown={handleEnterPress}
            defaultVariant={props.variant}
            defaultContrast={props.contrast}
        />
    )
}

const RenderActions = ({
    actions,
    campaign,
    show
}: {
    actions: ActionsProps
    campaign: CampaignProps
    show: boolean
}) => {
    if (!actions) {
        return null
    }
    return (
        <div
            className={styles.productPortfolio__actions}
            style={{ display: show ? 'flex' : 'none' }}>
            <RenderCTA
                index={1}
                campaign={campaign}
                variant="secondary"
                {...(actions?.cta_primary || null)}
                contrast={actions?.cta_primary?.contrast || 'dark'}
            />
            <RenderCTA
                index={2}
                campaign={campaign}
                variant="secondary"
                {...(actions?.cta_secondary || null)}
                contrast={actions?.cta_secondary?.contrast || 'dark'}
            />
            <RenderCTA
                index={3}
                campaign={campaign}
                variant="ghost"
                {...(actions?.cta_tertiary || null)}
                contrast={actions?.cta_tertiary?.contrast || 'dark'}
            />
        </div>
    )
}

const RenderHeader = ({ heading }: HeadingProps) => {
    if (!heading) {
        return null
    }

    return (
        <div className={styles.productPortfolio__header}>
            <header>
                <h3 className={styles.heading}>{heading}</h3>
            </header>
        </div>
    )
}

const RenderCard = ({ ...props }: Cards) => {
    const { isOpen } = useModal()
    const containerRef = useRef(null)
    const btnRef = useRef(null)

    const addViewPormotionTracking = !isOpen('coffees') && !isOpen('machines')

    useImpressionTracker(containerRef, props.campaign, addViewPormotionTracking)

    const [isHovered, setIsHovered] = useState(false)

    const handleFocus = () => setIsHovered(true)
    const handleBlur = event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsHovered(false)
        }
    }

    const handleMouseEnter = () => setIsHovered(true)
    const handleMouseLeave = () => setIsHovered(false)

    return (
        <div ref={containerRef} className={`${styles.productPortfolio__card}`}>
            <Card
                //style={{ opacity: isHovered ? '.5' : 'unset' }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                ref={btnRef}
                className={`${styles.productPortfolio__card} ${isHovered ? styles.isHovered : ''} `}
                title={props.heading}
                imgProps={{
                    alt: props.image?.mobile?.alt,
                    src: props.image?.mobile?.src,
                    srcSet: props.image?.mobile?.src + ` 2x`
                }}
                imgSources={[
                    {
                        media: '(min-width: 768px)',
                        srcSet: `${props.image?.desktop?.src} 1x, ${props.image?.desktop?.src} 2x`
                    }
                ]}>
                <RenderHeader heading={props.heading} />
                <RenderActions
                    show={isHovered}
                    actions={props.actions}
                    campaign={props.campaign}
                />
            </Card>
        </div>
    )
}

function ProductPortfolio({ data }: ProductPortfolioProps) {
    const pbProps = getPBProps<PBProps>(data)
    const { layout, copywriting } = pbProps as PBProps
    const layoutPadding = [layout.padding_top, layout.padding_bottom]

    return (
        <PbPropsProvider value={pbProps}>
            <ModalProvider>
                <div
                    className={`${styles['nb-product-portfolio']} ${[...layoutPadding].join(' ')}`}>
                    <RenderCards
                        cards={copywriting.cards}
                        modals={copywriting.modals}
                    />
                </div>
            </ModalProvider>
        </PbPropsProvider>
    )
}

export default ProductPortfolio
